import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomFieldOptionType, CustomFieldType } from 'components/forms/custom_fields/CustomFieldTypes';
import TextareaInput from 'components/forms/basefields/TextareaInput';
import SwitchButton from 'components/buttons/SwitchButton';
import DateInput from 'components/forms/basefields/DateInput';
import TimeInput from 'components/forms/basefields/TimeInput';
import { optionPresets } from 'components/forms/custom_fields/customFieldOptionPresets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/free-solid-svg-icons';

interface FormMobilePreviewProps {
    field: CustomFieldType;
    fields: CustomFieldType[];
}

const FormMobilePreview: React.FC<FormMobilePreviewProps> = ({
    field,
    fields,
}) => {
    const { t } = useTranslation();
    const [previewValue, setPreviewValue] = useState<any>(null);
    const [previewCheckboxValues, setPreviewCheckboxValues] = useState<string[]>([]);
    const [showOptionScreen, setShowOptionScreen] = useState(false);

    const fieldIdentifier = field.id || field.uuid;
    const label = field.label || t('custom_field.general.field_label_label');

    const fixedOptionFieldTypes = [
        'yes_no',
        'acceptable',
        'compliant',
    ];

    const selectableFieldTypes = [
        'dropdown',
        'tabs',
        'yes_no',
        'acceptable',
        'compliant',
    ];

    const isFixedOptionField = fixedOptionFieldTypes.includes(field.field_type);
    const isSelectField = selectableFieldTypes.includes(field.field_type);

    const previewOptions = useMemo(() => {
        const hasFieldOptions = (field.options || []).some(option => !option.deleted);

        if (isFixedOptionField && !hasFieldOptions) {
            return optionPresets[field.field_type] || [];
        }

        return field.options || [];
    }, [field.field_type, field.options, isFixedOptionField]);

    // Get the identifier for a custom field
    const getFieldIdentifier = (customField: CustomFieldType) => {
        return customField.id || customField.uuid;
    };

    // Determine wheter two condition values are the same
    const isSameConditionValue = (
        firstValue: string | number | boolean | null | undefined,
        secondValue: string | number | boolean | null | undefined
    ) => {
        return String(firstValue) === String(secondValue);
    };

    // Get the condition value for an option
    const getOptionConditionValue = (option: CustomFieldOptionType) => {
        return option.id || option.uuid || option.value || '';
    };

    // Get the child fields for this field
    const childFields = fields
        .filter(custom_field =>
            String(custom_field.parent_field_id) === String(fieldIdentifier) &&
            !custom_field.deleted
        )
        .sort((firstField, secondField) => firstField.ordering - secondField.ordering);

    // Determine wheter a child field should be shown
    const shouldShowChildField = (childField: CustomFieldType) => {
        if (field.field_type === 'boolean') {
            return isSameConditionValue(previewValue, childField.show_when_boolean);
        }

        if (field.field_type === 'checkbox') {
            return previewCheckboxValues.some(preview_checkbox_value =>
                isSameConditionValue(preview_checkbox_value, childField.show_when_option_id)
            );
        }

        return isSameConditionValue(previewValue, childField.show_when_option_id);
    };

    // Convert the label into a backend value
    const convertLabelToBackendValue = (label: string): string => {
        return label
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');
    };

    // Get the selected option label
    const getSelectedOptionLabel = () => {
        const selectedOption = previewOptions.find(option =>
            isSameConditionValue(getOptionConditionValue(option), previewValue)
        );

        return selectedOption?.label || t('custom_field.general.select_option_label');
    };

    // Handle select option
    const handleSelectOption = (option: CustomFieldOptionType) => {
        setPreviewValue(getOptionConditionValue(option));
        setShowOptionScreen(false);
    };

    const renderMobilePreview = () => {
        if (showOptionScreen) {
            return (
                <div className='ios-selection-screen'>
                    <div className='ios-preview-header'>
                        <button
                            type='button'
                            className='ios-preview-header-back-button'
                            onClick={() => setShowOptionScreen(false)}
                        >
                            <span className='ios-preview-header-back-icon'>
                                ‹
                            </span>

                            <span className='ios-preview-header-back-label'>
                                Terug
                            </span>
                        </button>

                        <strong className='ios-preview-header-title'>
                            {label}
                        </strong>

                        <span className='ios-preview-header-side' />
                    </div>

                    <div className='ios-selection-list'>
                        {previewOptions
                            .filter(option => !option.deleted)
                            .map(option => {
                                const optionValue = getOptionConditionValue(option);
                                const isSelected = isSameConditionValue(previewValue, optionValue);

                                return (
                                    <button
                                        key={option.id || option.uuid || option.value}
                                        type='button'
                                        className='ios-selection-option'
                                        onClick={() => handleSelectOption(option)}
                                    >
                                        <span>
                                            {t(option.label)}
                                        </span>

                                        {isSelected && (
                                            <span className='ios-checkmark'>
                                                ✓
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                    </div>
                </div>
            );
        }

        if (field.field_type === 'textarea') {
            return (
                <div className='ios-field-block'>
                    <div className='ios-textarea-title'>
                        {label}
                        {field.required && (
                            <span className='required'>
                                *
                            </span>
                        )}
                    </div>

                    <TextareaInput
                        name={convertLabelToBackendValue(label)}
                        rows={5}
                        value={previewValue || ''}
                        placeholder={field.placeholder || ''}
                        onChange={(value) => {
                            setPreviewValue(typeof value === 'string' ? value : value.target.value);
                        }}
                    />
                </div>
            );
        }

        if (isSelectField) {
            return (
                <button
                    type='button'
                    className='ios-table-cell ios-select-cell'
                    onClick={() => setShowOptionScreen(true)}
                >
                    <span className='ios-cell-label'>
                        {label}
                        {field.required && (
                            <span className='required'>
                                *
                            </span>
                        )}
                    </span>

                    <span className={`ios-cell-value ${previewValue ? '' : 'placeholder'}`}>
                        {t(getSelectedOptionLabel())}
                    </span>

                    <span className='ios-chevron'>
                        ›
                    </span>
                </button>
            );
        }

        if (field.field_type === 'checkbox') {
            return (
                <div className='ios-checkbox-section'>
                    <div className='ios-section-title'>
                        {label}
                        {field.required && (
                            <span className='required'>
                                *
                            </span>
                        )}
                    </div>

                    <div className='ios-table-group'>
                        {previewOptions
                            .filter(option => !option.deleted)
                            .map(option => {
                                const optionValue = String(getOptionConditionValue(option));
                                const isChecked = previewCheckboxValues.includes(optionValue);

                                return (
                                    <button
                                        key={option.id || option.uuid || option.value}
                                        type='button'
                                        className='ios-table-cell ios-checkbox-cell'
                                        onClick={() => {
                                            if (isChecked) {
                                                setPreviewCheckboxValues(current =>
                                                    current.filter(current_value => current_value !== optionValue)
                                                );
                                            } else {
                                                setPreviewCheckboxValues(current => [ ...current, optionValue ]);
                                            }
                                        }}
                                    >
                                        <span className='ios-cell-label'>
                                            {t(option.label)}
                                        </span>

                                        {isChecked && (
                                            <span className='ios-checkmark'>
                                                ✓
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                    </div>
                </div>
            );
        }

        if (field.field_type === 'boolean') {
            return (
                <div className='ios-table-cell ios-switch-cell'>
                    <span className='ios-cell-label'>
                        {label}
                        {field.required && (
                            <span className='required'>
                                *
                            </span>
                        )}
                    </span>

                    <SwitchButton
                        value={previewValue || false}
                        onToggle={(isOn) => {
                            setPreviewValue(isOn);
                        }}
                    />
                </div>
            );
        }

        if (field.field_type === 'amount') {
            return (
                <div className='ios-table-cell ios-input-cell'>
                    <span className='ios-cell-label'>
                        {label}
                        {field.required && (
                            <span className='required'>
                                *
                            </span>
                        )}
                    </span>

                    <div className='ios-input-value amount'>
                        <span>
                            €
                        </span>

                        <input
                            name={convertLabelToBackendValue(label)}
                            placeholder='0,00'
                            value={previewValue || ''}
                            onChange={(event) => {
                                const cleanedValue = event.target.value.replace(/[^0-9.,-]/g, '');
                                setPreviewValue(cleanedValue);
                            }}
                        />
                    </div>
                </div>
            );
        }

        if (field.field_type === 'number') {
            return (
                <div className='ios-table-cell ios-input-cell'>
                    <span className='ios-cell-label'>
                        {label}
                        {field.required && (
                            <span className='required'>
                                *
                            </span>
                        )}
                    </span>

                    <input
                        name={convertLabelToBackendValue(label)}
                        placeholder='0'
                        value={previewValue || ''}
                        onChange={(event) => {
                            const cleanedValue = event.target.value.replace(/[^0-9.,-]/g, '');
                            setPreviewValue(cleanedValue);
                        }}
                    />
                </div>
            );
        }

        if (field.field_type === 'date' || field.field_type === 'time') {
            return (
                <div className='ios-table-cell ios-preview-action-cell ios-date-cell'>
                    <span className='ios-cell-label'>
                        {label}
                        {field.required && (
                            <span className='required'>
                                *
                            </span>
                        )}
                    </span>

                    {field.field_type === 'date' ? (
                        <DateInput
                            name={convertLabelToBackendValue(label)}
                            value={previewValue || null}
                            onChange={(value) => setPreviewValue(value)}
                        />
                    ) : (
                        <TimeInput
                            type='time'
                            name={convertLabelToBackendValue(label)}
                            value={previewValue || null}
                            onChange={(value) => setPreviewValue(value)}
                        />
                    )}
                </div>
            );
        }

        if (field.field_type === 'photo') {
            return (
                <div className='ios-table-cell ios-preview-action-cell ios-photo-cell'>
                    <span className='ios-cell-label'>
                        {label}
                        {field.required && (
                            <span className='required'>
                                *
                            </span>
                        )}
                    </span>

                    <span className='ios-preview-action-placeholder'>
                        Maak foto
                    </span>

                    <FontAwesomeIcon
                        icon={faCamera}
                        className='ios-preview-action-icon'
                    />
                </div>
            );
        }

        if (field.field_type === 'signature') {
            return (
                <div className='ios-table-cell ios-preview-action-cell ios-signature-cell'>
                    <span className='ios-cell-label'>
                        {label}
                        {field.required && (
                            <span className='required'>
                                *
                            </span>
                        )}
                    </span>

                    <span className='ios-preview-action-placeholder'>
                        Plaats handtekening
                    </span>
                </div>
            );
        }

        return (
            <div className='ios-table-cell ios-input-cell'>
                <span className='ios-cell-label'>
                    {label}
                    {field.required && (
                        <span className='required'>
                            *
                        </span>
                    )}
                </span>

                <input
                    name={convertLabelToBackendValue(label)}
                    placeholder={field.placeholder || ''}
                    value={previewValue || ''}
                    onChange={(event) => {
                        setPreviewValue(event.target.value);
                    }}
                />
            </div>
        );
    };

    return (
        <>
            <div className='ios-preview-field'>
                {renderMobilePreview()}

                {field.description && !showOptionScreen && (
                    <div className='ios-helper-text'>
                        {field.description}
                    </div>
                )}
            </div>

            {!showOptionScreen && childFields
                .filter(child_field => shouldShowChildField(child_field))
                .map(child_field => (
                    <FormMobilePreview
                        key={getFieldIdentifier(child_field)}
                        field={child_field}
                        fields={fields}
                    />
                ))}
        </>
    );
};

export default FormMobilePreview;